import React, { useState, useRef, useEffect } from 'react';
import {
  Upload, Library, FileText, CheckCircle2, Save, Search, ChevronDown, ChevronRight,
  Plus, X, Eye, EyeOff, Loader2, Sparkles, ToggleLeft, ToggleRight, Table2, Image,
  PenTool, GripVertical, Trash2, ArrowUp, ArrowDown, Settings, ImagePlus
} from 'lucide-react';
import TemplateCard from './TemplateCard';

const CATEGORIES = ['All', 'Compliance', 'Onboarding', 'Safety', 'Operations', 'Finance', 'Other'];

const PAGE_SIZES = [
  { id: 'letter', name: 'Letter (8.5" x 11")', width: 8.5, height: 11 },
  { id: 'a4', name: 'A4 (8.27" x 11.69")', width: 8.27, height: 11.69 },
  { id: 'legal', name: 'Legal (8.5" x 14")', width: 8.5, height: 14 },
];

const DocumentSetup = ({
  isDark, topic, style, styles, templateFile, selectedTemplate,
  templateStructure, templates, loadingTemplates, isParsing, isGenerating,
  sectionInstructions, documentSettings, onDocumentSettingsChange,
  onTopicChange, onStyleChange, onFileUpload, onSelectTemplate,
  onParse, onToggleSection, onAddSection, onSectionInstructionChange,
  onGenerate, onSaveTemplate, onDeleteTemplate, onBack,
  onCreateFromScratch, onClearTemplate
}) => {
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  const [showSettings, setShowSettings] = useState(false);
  const [templateSource, setTemplateSource] = useState('upload'); // 'upload' | 'library' | 'scratch'
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateCategory, setTemplateCategory] = useState('All');
  const [addingSectionAt, setAddingSectionAt] = useState(null);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // Scratch builder state
  const [scratchSections, setScratchSections] = useState([
    { id: crypto.randomUUID(), title: '' },
  ]);

  // Auto-switch to library if user has templates but no upload
  useEffect(() => {
    if (selectedTemplate && !templateFile) setTemplateSource('library');
  }, [selectedTemplate]);

  // Clear stale state when switching template source
  const switchTemplateSource = (source) => {
    if (source !== templateSource) {
      // Clear previous selection when switching tabs
      if (source === 'scratch') {
        onClearTemplate();
      } else if (source === 'upload' && selectedTemplate) {
        onClearTemplate();
      } else if (source === 'library' && templateFile) {
        onClearTemplate();
      }
      setPreviewTemplate(null);
    }
    setTemplateSource(source);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.docx')) onFileUpload(file);
  };

  const handleAddSection = (afterId) => {
    if (!newSectionTitle.trim()) return;
    onAddSection(afterId, newSectionTitle.trim());
    setNewSectionTitle('');
    setAddingSectionAt(null);
  };

  // Scratch builder helpers
  const addScratchSection = () => {
    setScratchSections(prev => [...prev, { id: crypto.randomUUID(), title: '' }]);
  };

  const updateScratchTitle = (id, title) => {
    setScratchSections(prev => prev.map(s => s.id === id ? { ...s, title } : s));
  };

  const removeScratchSection = (id) => {
    setScratchSections(prev => prev.length > 1 ? prev.filter(s => s.id !== id) : prev);
  };

  const moveScratchSection = (id, direction) => {
    setScratchSections(prev => {
      const idx = prev.findIndex(s => s.id === id);
      if (idx < 0) return prev;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  };

  const handleBuildFromScratch = () => {
    const validSections = scratchSections.filter(s => s.title.trim());
    if (validSections.length === 0) return;
    onCreateFromScratch(validSections.map(s => s.title.trim()));
  };

  const scratchReady = scratchSections.some(s => s.title.trim());

  const filteredTemplates = templates.filter(t => {
    const matchesSearch = !templateSearch || t.name.toLowerCase().includes(templateSearch.toLowerCase());
    const matchesCategory = templateCategory === 'All' || t.category === templateCategory;
    return matchesSearch && matchesCategory;
  });

  // Recently used templates (sorted by use_count)
  const recentTemplates = [...templates].sort((a, b) => (b.use_count || 0) - (a.use_count || 0)).slice(0, 3);

  const includedCount = templateStructure?.sections?.filter(s => s.included !== false).length || 0;

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className={`grid gap-6 ${templateStructure ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 max-w-2xl mx-auto'}`}>

        {/* ==================== LEFT COLUMN: Template + Config ==================== */}
        <div className="space-y-5">

          {/* Topic */}
          <div>
            <label className="block text-sm font-medium mb-1.5">Training Topic</label>
            <input
              type="text"
              value={topic}
              onChange={(e) => onTopicChange(e.target.value)}
              placeholder="e.g., Management Occupancy Review 2026, Fair Housing Compliance..."
              className={`w-full px-4 py-3 rounded-xl border text-sm ${
                isDark
                  ? 'bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Style */}
          <div>
            <label className="block text-sm font-medium mb-1.5">Content Style</label>
            <div className="grid grid-cols-3 gap-2">
              {styles.map(s => (
                <button
                  key={s.id}
                  onClick={() => onStyleChange(s.id)}
                  className={`px-3 py-2 rounded-lg border text-sm text-left transition-colors ${
                    style === s.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : isDark ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-xs">{s.name}</div>
                  <div className={`text-[10px] mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{s.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Document Settings (always available, collapsible) */}
          <div>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`flex items-center gap-2 text-xs font-medium transition-colors ${
                isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Settings size={13} />
              Document Settings
              <ChevronDown size={12} className={`transition-transform ${showSettings ? 'rotate-180' : ''}`} />
            </button>

            {showSettings && (
              <div className={`mt-2 p-3 rounded-lg border space-y-3 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
                {/* Logo */}
                <div>
                  <label className="block text-xs font-medium mb-1">Logo</label>
                  <div className="flex items-center gap-3">
                    {documentSettings?.logo_base64 ? (
                      <div className="relative">
                        <img
                          src={`data:image/png;base64,${documentSettings.logo_base64}`}
                          alt="Logo"
                          className="h-10 max-w-[120px] object-contain rounded border border-gray-200 dark:border-gray-600"
                        />
                        <button
                          onClick={() => onDocumentSettingsChange({ ...documentSettings, logo_base64: null, logo_name: null })}
                          className="absolute -top-1 -right-1 p-0.5 rounded-full bg-red-500 text-white"
                        >
                          <X size={8} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => logoRef.current?.click()}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed text-xs transition-colors ${
                          isDark ? 'border-gray-600 text-gray-400 hover:border-gray-500' : 'border-gray-300 text-gray-500 hover:border-gray-400'
                        }`}
                      >
                        <ImagePlus size={12} /> Upload Logo
                      </button>
                    )}
                    <input
                      ref={logoRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = () => {
                          const b64 = reader.result.split(',')[1];
                          onDocumentSettingsChange({ ...documentSettings, logo_base64: b64, logo_name: file.name });
                        };
                        reader.readAsDataURL(file);
                        e.target.value = '';
                      }}
                    />
                  </div>
                </div>

                {/* Header / Footer */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium mb-1">Header Text</label>
                    <input
                      type="text"
                      value={documentSettings?.header_text || ''}
                      onChange={(e) => onDocumentSettingsChange({ ...documentSettings, header_text: e.target.value })}
                      placeholder="e.g., Company Name"
                      className={`w-full px-2 py-1.5 rounded border text-xs ${
                        isDark ? 'bg-gray-750 border-gray-600 text-gray-200 placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Footer Text</label>
                    <input
                      type="text"
                      value={documentSettings?.footer_text || ''}
                      onChange={(e) => onDocumentSettingsChange({ ...documentSettings, footer_text: e.target.value })}
                      placeholder="e.g., Confidential"
                      className={`w-full px-2 py-1.5 rounded border text-xs ${
                        isDark ? 'bg-gray-750 border-gray-600 text-gray-200 placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    />
                  </div>
                </div>

                {/* Page Size */}
                <div>
                  <label className="block text-xs font-medium mb-1">Page Size</label>
                  <div className="flex gap-2">
                    {PAGE_SIZES.map(ps => (
                      <button
                        key={ps.id}
                        onClick={() => onDocumentSettingsChange({ ...documentSettings, page_size: ps.id })}
                        className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                          (documentSettings?.page_size || 'letter') === ps.id
                            ? 'bg-blue-500 text-white'
                            : isDark ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        {ps.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Page Numbers */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={documentSettings?.page_numbers !== false}
                    onChange={(e) => onDocumentSettingsChange({ ...documentSettings, page_numbers: e.target.checked })}
                    className="w-3.5 h-3.5 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-xs">Include page numbers in footer</span>
                </label>
              </div>
            )}
          </div>

          {/* Template Source Toggle */}
          <div>
            <label className="block text-sm font-medium mb-1.5">Template</label>
            <div className={`flex rounded-lg p-0.5 mb-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <button
                onClick={() => switchTemplateSource('upload')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  templateSource === 'upload'
                    ? isDark ? 'bg-gray-600 text-white' : 'bg-white text-gray-900 shadow-sm'
                    : isDark ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                <Upload size={13} /> Upload
              </button>
              <button
                onClick={() => switchTemplateSource('library')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  templateSource === 'library'
                    ? isDark ? 'bg-gray-600 text-white' : 'bg-white text-gray-900 shadow-sm'
                    : isDark ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                <Library size={13} /> Library
              </button>
              <button
                onClick={() => switchTemplateSource('scratch')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  templateSource === 'scratch'
                    ? isDark ? 'bg-gray-600 text-white' : 'bg-white text-gray-900 shadow-sm'
                    : isDark ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                <PenTool size={13} /> From Scratch
              </button>
            </div>

            {templateSource === 'upload' ? (
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                  templateFile
                    ? 'border-green-400 bg-green-50 dark:bg-green-900/10'
                    : isDark ? 'border-gray-600 hover:border-blue-500 hover:bg-gray-800' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".docx"
                  className="hidden"
                  onChange={(e) => { if (e.target.files[0]) onFileUpload(e.target.files[0]); }}
                />
                {templateFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <CheckCircle2 size={20} className="text-green-500" />
                    <div className="text-left">
                      <p className="font-medium text-sm">{templateFile.name}</p>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {(templateFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); onClearTemplate(); }}
                      title="Remove template"
                      className={`p-1 rounded-full transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload size={28} className={`mx-auto mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <p className="text-sm font-medium">Drop .docx or click to upload</p>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {/* Recently used */}
                {recentTemplates.length > 0 && (
                  <div>
                    <div className={`text-[10px] uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      Recently Used
                    </div>
                    <div className="space-y-1">
                      {recentTemplates.map(t => {
                        const isSelected = selectedTemplate?.id === t.id;
                        return (
                          <div
                            key={t.id}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                              isSelected
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700'
                                : isDark ? 'hover:bg-gray-700 border border-transparent' : 'hover:bg-gray-100 border border-transparent'
                            }`}
                          >
                            <button
                              onClick={() => isSelected ? onClearTemplate() : onSelectTemplate(t)}
                              className="flex-1 text-left flex items-center gap-2 min-w-0"
                            >
                              {isSelected
                                ? <CheckCircle2 size={14} className="shrink-0 text-blue-500" />
                                : <FileText size={14} className="shrink-0 text-blue-500" />
                              }
                              <span className="truncate font-medium text-xs">{t.name}</span>
                              {t.is_shared && (
                                <span className="ml-auto text-[9px] px-1 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                                  Shared
                                </span>
                              )}
                            </button>
                            {isSelected && (
                              <button
                                onClick={() => onClearTemplate()}
                                title="Remove selection"
                                className={`p-0.5 rounded shrink-0 transition-colors ${isDark ? 'hover:bg-blue-800 text-blue-400' : 'hover:bg-blue-200 text-blue-500'}`}
                              >
                                <X size={13} />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Full library with search */}
                <div>
                  <div className="relative mb-2">
                    <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <input
                      type="text"
                      value={templateSearch}
                      onChange={(e) => setTemplateSearch(e.target.value)}
                      placeholder="Search templates..."
                      className={`w-full pl-8 pr-3 py-1.5 rounded-lg border text-xs ${
                        isDark
                          ? 'bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500'
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    />
                  </div>
                  <div className="flex gap-1 flex-wrap mb-2">
                    {CATEGORIES.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setTemplateCategory(cat)}
                        className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                          templateCategory === cat
                            ? 'bg-blue-500 text-white'
                            : isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                  <div className="max-h-48 overflow-auto space-y-1">
                    {loadingTemplates ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 size={16} className="animate-spin text-blue-500" />
                      </div>
                    ) : filteredTemplates.length === 0 ? (
                      <p className={`text-xs text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        No templates found
                      </p>
                    ) : (
                      filteredTemplates.map(t => {
                        const isSelected = selectedTemplate?.id === t.id;
                        return (
                          <div key={t.id} className={`flex items-center gap-1 rounded-lg text-xs transition-colors ${
                            isSelected
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                              : isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                          }`}>
                            <button
                              onClick={() => isSelected ? onClearTemplate() : onSelectTemplate(t)}
                              className="flex-1 text-left px-3 py-2 flex items-center gap-2 min-w-0"
                            >
                              {isSelected
                                ? <CheckCircle2 size={12} className="shrink-0 text-blue-500" />
                                : <FileText size={12} className="shrink-0" />
                              }
                              <div className="min-w-0 flex-1">
                                <div className="font-medium truncate">{t.name}</div>
                                {t.description && (
                                  <div className={`truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t.description}</div>
                                )}
                              </div>
                              {t.category && (
                                <span className={`text-[9px] px-1 py-0.5 rounded shrink-0 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                  {t.category}
                                </span>
                              )}
                            </button>
                            {isSelected && (
                              <button
                                onClick={() => onClearTemplate()}
                                title="Remove selection"
                                className={`p-1 rounded shrink-0 transition-colors ${isDark ? 'hover:bg-blue-800 text-blue-400' : 'hover:bg-blue-200 text-blue-500'}`}
                              >
                                <X size={13} />
                              </button>
                            )}
                            {!isSelected && t.template_structure && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setPreviewTemplate(previewTemplate?.id === t.id ? null : t); }}
                                title="Preview sections"
                                className={`p-1.5 rounded shrink-0 mr-1 transition-colors ${
                                  previewTemplate?.id === t.id
                                    ? 'text-blue-500'
                                    : isDark ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
                                }`}
                              >
                                <Eye size={12} />
                              </button>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Build from Scratch */}
            {templateSource === 'scratch' && (
              <div className="space-y-2">
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Define your document sections. AI will generate content for each one.
                </p>
                <div className="space-y-1.5">
                  {scratchSections.map((section, idx) => (
                    <div key={section.id} className="flex items-center gap-1.5">
                      <span className={`text-[10px] w-5 text-center shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {idx + 1}
                      </span>
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) => updateScratchTitle(section.id, e.target.value)}
                        placeholder={idx === 0 ? 'e.g., Introduction' : `Section ${idx + 1} title...`}
                        className={`flex-1 px-2.5 py-1.5 rounded-lg border text-xs ${
                          isDark
                            ? 'bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-600'
                            : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                        } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addScratchSection();
                          }
                        }}
                      />
                      <div className="flex items-center shrink-0">
                        <button
                          onClick={() => moveScratchSection(section.id, -1)}
                          disabled={idx === 0}
                          className={`p-0.5 rounded transition-colors disabled:opacity-20 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                        >
                          <ArrowUp size={11} />
                        </button>
                        <button
                          onClick={() => moveScratchSection(section.id, 1)}
                          disabled={idx === scratchSections.length - 1}
                          className={`p-0.5 rounded transition-colors disabled:opacity-20 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                        >
                          <ArrowDown size={11} />
                        </button>
                        <button
                          onClick={() => removeScratchSection(section.id)}
                          disabled={scratchSections.length <= 1}
                          className={`p-0.5 rounded transition-colors disabled:opacity-20 ${isDark ? 'text-gray-400 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}
                        >
                          <X size={11} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={addScratchSection}
                  className={`flex items-center gap-1 text-xs transition-colors ${
                    isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                  }`}
                >
                  <Plus size={12} /> Add section
                </button>
              </div>
            )}

            {/* Save template link */}
            {templateFile && templateSource === 'upload' && (
              <button
                onClick={onSaveTemplate}
                className={`flex items-center gap-1.5 text-xs mt-2 transition-colors ${
                  isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                }`}
              >
                <Save size={12} /> Save to library
              </button>
            )}
          </div>

          {/* Template Preview (when hovering a library template) */}
          {previewTemplate?.template_structure && templateSource === 'library' && !templateStructure && (
            <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium truncate flex-1">{previewTemplate.name}</div>
                <button onClick={() => setPreviewTemplate(null)} className={`p-1 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                  <X size={14} className="text-gray-400" />
                </button>
              </div>
              {previewTemplate.description && (
                <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{previewTemplate.description}</p>
              )}
              <div className={`text-[10px] uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {previewTemplate.template_structure.stats?.section_count || 0} sections
              </div>
              <div className="space-y-1 max-h-48 overflow-auto">
                {previewTemplate.template_structure.sections?.map((s, i) => (
                  <div key={s.id || i} className={`flex items-center gap-2 px-2 py-1 rounded text-xs ${
                    isDark ? 'bg-gray-750' : 'bg-gray-50'
                  }`}>
                    <span className={`${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{i + 1}.</span>
                    <span className={s.level <= 1 ? 'font-medium' : 'pl-3'}>{s.title}</span>
                    {s.has_image_slot && <Image size={9} className="text-gray-400 ml-auto" />}
                  </div>
                ))}
              </div>
              <button
                onClick={() => { onSelectTemplate(previewTemplate); setPreviewTemplate(null); }}
                className="mt-3 w-full py-1.5 rounded-lg bg-blue-500 text-white text-xs font-medium hover:bg-blue-600 transition-colors"
              >
                Use This Template
              </button>
            </div>
          )}

          {/* Parse / Generate / Build Buttons */}
          {templateSource === 'scratch' ? (
            !templateStructure ? (
              <button
                onClick={handleBuildFromScratch}
                disabled={!scratchReady || !topic.trim()}
                className={`w-full py-3 rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                  !scratchReady || !topic.trim()
                    ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                <PenTool size={16} /> Create Template ({scratchSections.filter(s => s.title.trim()).length} sections)
              </button>
            ) : (
              <button
                onClick={onGenerate}
                disabled={isGenerating || includedCount === 0}
                className={`w-full py-3 rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                  isGenerating || includedCount === 0
                    ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                {isGenerating ? (
                  <><Loader2 size={16} className="animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles size={16} /> Generate Content ({includedCount} sections)</>
                )}
              </button>
            )
          ) : !templateStructure ? (
            <button
              onClick={onParse}
              disabled={isParsing || (!templateFile && !selectedTemplate) || !topic.trim()}
              className={`w-full py-3 rounded-xl font-medium text-sm transition-colors ${
                isParsing || (!templateFile && !selectedTemplate) || !topic.trim()
                  ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              {isParsing ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={16} className="animate-spin" /> Parsing Template...
                </span>
              ) : (
                'Parse Template'
              )}
            </button>
          ) : (
            <button
              onClick={onGenerate}
              disabled={isGenerating || includedCount === 0}
              className={`w-full py-3 rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                isGenerating || includedCount === 0
                  ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
            >
              {isGenerating ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Generating...
                </>
              ) : (
                <>
                  <Sparkles size={16} /> Generate Content ({includedCount} sections)
                </>
              )}
            </button>
          )}
        </div>

        {/* ==================== RIGHT COLUMN: Structure Review ==================== */}
        {templateStructure && (
          <div className={`rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
            {/* Stats bar */}
            <div className={`flex items-center gap-4 px-4 py-3 border-b text-xs ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
              <span className="flex items-center gap-1">
                <FileText size={12} /> {templateStructure.stats?.section_count || 0} sections
              </span>
              {templateStructure.stats?.table_count > 0 && (
                <span className="flex items-center gap-1">
                  <Table2 size={12} /> {templateStructure.stats.table_count} tables
                </span>
              )}
              {templateStructure.stats?.image_count > 0 && (
                <span className="flex items-center gap-1">
                  <Image size={12} /> {templateStructure.stats.image_count} images
                </span>
              )}
              <span className="flex items-center gap-1">
                <Sparkles size={12} /> {templateStructure.stats?.placeholder_count || 0} placeholders
              </span>
              {templateStructure.pdf_preview && (
                <button
                  onClick={() => setShowPdfPreview(!showPdfPreview)}
                  className={`ml-auto flex items-center gap-1 transition-colors ${isDark ? 'hover:text-gray-300' : 'hover:text-gray-700'}`}
                >
                  {showPdfPreview ? <EyeOff size={12} /> : <Eye size={12} />}
                  Preview
                </button>
              )}
            </div>

            {/* Section list */}
            <div className="max-h-[60vh] overflow-auto">
              {showPdfPreview && templateStructure.pdf_preview ? (
                <div className="p-4">
                  <iframe
                    src={`data:application/pdf;base64,${templateStructure.pdf_preview}`}
                    className="w-full h-96 rounded-lg border"
                    title="Template preview"
                  />
                </div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {templateStructure.sections?.map((section, i) => (
                    <div key={section.id}>
                      <div className={`flex items-start gap-3 px-4 py-3 ${
                        section.included === false ? 'opacity-50' : ''
                      }`}>
                        {/* Toggle */}
                        <button
                          onClick={() => onToggleSection(section.id)}
                          className={`mt-0.5 shrink-0 transition-colors ${
                            section.included !== false
                              ? 'text-green-500'
                              : isDark ? 'text-gray-600' : 'text-gray-300'
                          }`}
                        >
                          {section.included !== false ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                        </button>

                        <div className="flex-1 min-w-0">
                          {/* Title */}
                          <div className="flex items-center gap-2">
                            <span className={`font-medium text-sm ${section.included === false ? 'line-through' : ''}`}>
                              {section.title}
                            </span>
                            {section.type === 'intro' && (
                              <span className={`text-[9px] px-1 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                                Intro
                              </span>
                            )}
                            {section.is_new && (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                                New
                              </span>
                            )}
                            {section.has_image_slot && (
                              <Image size={10} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                            )}
                          </div>

                          {/* Content preview */}
                          {section.content_blocks?.length > 0 && (
                            <p className={`text-[10px] mt-0.5 truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              {section.content_blocks.filter(b => b.text).map(b => b.text).join(' ').slice(0, 80)}
                              {section.content_blocks.some(b => b.is_placeholder) && ' [has placeholders]'}
                            </p>
                          )}

                          {/* Instructions textarea */}
                          {section.included !== false && (
                            <textarea
                              value={sectionInstructions[section.id] || ''}
                              onChange={(e) => onSectionInstructionChange(section.id, e.target.value)}
                              placeholder="Optional: specific instructions for this section..."
                              className={`w-full mt-1.5 px-2 py-1 rounded border text-[11px] resize-none transition-colors ${
                                isDark
                                  ? 'bg-gray-750 border-gray-600 text-gray-300 placeholder-gray-600 focus:border-blue-500'
                                  : 'bg-gray-50 border-gray-200 text-gray-700 placeholder-gray-400 focus:border-blue-500'
                              } focus:outline-none`}
                              rows={1}
                              onFocus={(e) => { e.target.style.minHeight = '48px'; }}
                              onBlur={(e) => { if (!e.target.value) e.target.style.minHeight = '24px'; }}
                              style={{ minHeight: '24px' }}
                            />
                          )}
                        </div>
                      </div>

                      {/* Add section after */}
                      {addingSectionAt === section.id ? (
                        <div className={`mx-4 mb-3 p-2 rounded-lg border-2 border-dashed ${isDark ? 'border-blue-800 bg-blue-900/10' : 'border-blue-200 bg-blue-50'}`}>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={newSectionTitle}
                              onChange={(e) => setNewSectionTitle(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleAddSection(section.id); }}
                              placeholder="New section title..."
                              className={`flex-1 px-2 py-1 rounded border text-xs ${
                                isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                              } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                              autoFocus
                            />
                            <button
                              onClick={() => handleAddSection(section.id)}
                              disabled={!newSectionTitle.trim()}
                              className="px-2 py-1 text-xs rounded bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
                            >
                              Add
                            </button>
                            <button onClick={() => { setAddingSectionAt(null); setNewSectionTitle(''); }}>
                              <X size={14} className="text-gray-400" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={`h-0 group relative ${i < templateStructure.sections.length - 1 ? '' : 'mb-2'}`}>
                          <button
                            onClick={() => setAddingSectionAt(section.id)}
                            className={`absolute left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] ${
                              isDark ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                          >
                            <Plus size={10} /> Add section
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add section at end */}
                  {addingSectionAt === 'end' ? (
                    <div className={`mx-4 my-3 p-2 rounded-lg border-2 border-dashed ${isDark ? 'border-blue-800 bg-blue-900/10' : 'border-blue-200 bg-blue-50'}`}>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newSectionTitle}
                          onChange={(e) => setNewSectionTitle(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleAddSection('end'); }}
                          placeholder="New section title..."
                          className={`flex-1 px-2 py-1 rounded border text-xs ${
                            isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                          } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                          autoFocus
                        />
                        <button
                          onClick={() => handleAddSection('end')}
                          disabled={!newSectionTitle.trim()}
                          className="px-2 py-1 text-xs rounded bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
                        >
                          Add
                        </button>
                        <button onClick={() => { setAddingSectionAt(null); setNewSectionTitle(''); }}>
                          <X size={14} className="text-gray-400" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="px-4 py-3">
                      <button
                        onClick={() => setAddingSectionAt('end')}
                        className={`flex items-center gap-1.5 text-xs transition-colors ${
                          isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <Plus size={13} /> Add section at end
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentSetup;
